/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-param-reassign */
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import {Box} from '../box';
import {TransparentButton} from '../button/transparent-button';
import {PauseIcon} from '../icons/pause';
import {PlayIcon} from '../icons/play';
import {ActivityIndicator} from '../activity-indicator';
import {Typography} from '../typography';
import {FrameworkImage} from '../image-renderer';
import {MusicTrackSlider} from '../music-track-player';
import {getSongDuration} from './utils';

interface MusicTrackPlayerProps {
  artist?: string;
  songTitle?: string;
  isPlaying: boolean;
  isLoading: boolean;
  togglePlayPause: () => void;
  currentAudio?: string;
  setAudioSrc: any;
  audioPlayer: any;
  audioSrc: string;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
}

const MusicTrackPlayer: React.FC<MusicTrackPlayerProps> = ({
  artist,
  songTitle,
  isPlaying,
  isLoading,
  audioSrc,
  setAudioSrc,
  currentAudio,
  audioPlayer,
  togglePlayPause,
  setIsPlaying,
}: MusicTrackPlayerProps) => {
  const changeTrack = () => {
    if (currentAudio !== audioSrc) {
      setAudioSrc(audioSrc);
      audioPlayer.current?.pause();
      audioPlayer.current?.setAttribute('src', audioSrc);
      audioPlayer.current?.load();
      audioPlayer.current?.play();
      setIsPlaying(true);
    } else {
      togglePlayPause();
    }
  };

  return (
    <Box
      width="100%"
      alignItems="center"
      borderStyle="solid"
      borderColor="outlines"
      borderWidth="one"
      borderLeftWidth="zero"
      borderBottomWidth="zero"
      borderRightWidth="zero"
      bg="surfaceDark"
    >
      <Box
        width="100%"
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        padding="two"
      >
        <Box flex="1">
          <Typography textStyle="s" color="textDefault">
            {songTitle}
          </Typography>
          <Typography textStyle="s" color="textLow">
            {artist}
          </Typography>
        </Box>
        <Box>
          <TransparentButton onPress={changeTrack}>
            {isLoading ? (
              <Box
                bg="surfaceDark"
                width={38}
                height={38}
                borderRadius="circle"
                borderWidth="one"
                borderColor="outlines"
                borderStyle="solid"
                alignItems="center"
                justifyContent="center"
              >
                <ActivityIndicator color="outlines" size={16} />
              </Box>
            ) : null}
            {!isPlaying && !isLoading ? (
              <Box
                bg="surfaceDark"
                width={38}
                height={38}
                borderRadius="circle"
                borderWidth="one"
                borderColor="outlines"
                borderStyle="solid"
                alignItems="center"
                justifyContent="center"
              >
                <Box
                  position="absolute"
                  top="12px"
                  left="14px"
                  css={{userSelect: 'none'}}
                >
                  <PlayIcon size={12} />
                </Box>
              </Box>
            ) : null}
            {isPlaying && !isLoading ? (
              <Box
                bg="outlines"
                width={38}
                height={38}
                borderRadius="circle"
                borderWidth="one"
                borderColor="outlines"
                borderStyle="solid"
                alignItems="center"
                justifyContent="center"
              >
                <PauseIcon size={12} />
              </Box>
            ) : null}
          </TransparentButton>
        </Box>
      </Box>
    </Box>
  );
};

export interface RemixCardProps {
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  setCurrentTime: Dispatch<SetStateAction<number>>;
  audioPlayer: MutableRefObject<HTMLAudioElement | null>;
  artist?: string;
  songTitle?: string;
  togglePlayPause: () => void;
  imageSrc: string;
  setAudioSrc: any;
  audioSrc: string;
  currentAudio?: string;
  setIsPlaying: Dispatch<SetStateAction<boolean>>;
}

export const RemixCard: React.FC<RemixCardProps> = ({
  isPlaying,
  setCurrentTime,
  audioPlayer,
  currentTime,
  artist,
  songTitle,
  isLoading,
  togglePlayPause,
  imageSrc,
  setAudioSrc,
  audioSrc,
  currentAudio,
  setIsPlaying,
}: RemixCardProps) => {
  const [songDuration, setSongDuration] = useState<number>(0);

  useEffect(() => {
    getSongDuration(audioSrc).then((length) => {
      setSongDuration(length);
    });
  }, [audioSrc]);

  return (
    <Box>
      <Box
        width="100%"
        style={{paddingBottom: '100%'}}
        borderStyle="solid"
        borderColor="outlines"
        borderTopWidth="one"
        borderBottomWidth="zero"
        borderLeftWidth="zero"
        borderRightWidth="zero"
      >
        <FrameworkImage src={imageSrc} layout="fill" objectFit="cover" />
        <Box w="100%" position="absolute" bottom={0}>
          <MusicTrackSlider
            isPlaying={isPlaying}
            duration={songDuration}
            setCurrentTime={setCurrentTime}
            audioPlayer={audioPlayer}
            currentTime={currentTime}
            remixCard
          />
        </Box>
      </Box>
      <MusicTrackPlayer
        setAudioSrc={setAudioSrc}
        audioSrc={audioSrc}
        currentAudio={currentAudio}
        audioPlayer={audioPlayer}
        artist={artist}
        songTitle={songTitle}
        isPlaying={isPlaying}
        isLoading={isLoading}
        setIsPlaying={setIsPlaying}
        togglePlayPause={togglePlayPause}
      />
    </Box>
  );
};
